/*
 * ero-shimmer.js — pointer-driven shimmer + tilt for
 * Section 7 ingredient cards.
 *
 * Phase B: desktop pointer-driven only. Mobile/touch is
 * Phase C (gyroscope, gated behind first-swipe permission).
 *
 * Architecture:
 * - Per-card pointer listeners (pointerenter, pointermove,
 *   pointerleave) on .ero-it__card
 * - GSAP quickTo for smoothed CSS custom property updates:
 *   0.4s power2.out for tilt (--tilt-x, --tilt-y),
 *   0.2s power2.out for shimmer position (--shimmer-x,
 *   --shimmer-y)
 * - Tilt: standard direction, ±6° max, applied to
 *   .ero-it__slide.active .ero-it__card via the CSS
 *   transform that consumes --tilt-x/--tilt-y
 * - Shimmer: opposite direction (Pokemon foil convention),
 *   range derived from -100% to 0% to keep conic rotation
 *   center off-card at all pointer positions
 * - Active card class is managed by the carousel JS; tilt
 *   only renders on active because the CSS transform
 *   selector is .ero-it__slide.active .ero-it__card
 * - Carousel coexistence: track owns pointer capture during
 *   drag (setPointerCapture); shimmer listeners go silent
 *   during a drag and resume on release. Acceptable UX —
 *   tilt-freeze during drag is unnoticeable because the
 *   swipe motion dominates.
 * - Activation conditions composed into a single matchMedia
 *   query (Phase 0 refactor, 2026-05-18): width ≥ 990px,
 *   hover support, fine pointer, no reduced-motion. When the
 *   viewport crosses to mobile, gsap.matchMedia tears down
 *   the quickTos automatically; the cleanup function below
 *   also removes the pointer listeners and drops the cached
 *   card state. Touch devices and below-990 viewports get
 *   the static Phase A treatment until Phase C lands.
 * - Theme editor support: rebinds on shopify:section:load,
 *   cleans up on shopify:section:unload.
 *
 * Technique inspired by simeydotme/pokemon-cards-css
 * (GPL-3.0). Clean-room reimplementation — see
 * LICENSES.txt.
 */

(function () {
  'use strict';

  // card → { tiltX, tiltY, shimmerX, shimmerY, onEnter, onMove, onLeave }
  // WeakMap so removed cards (e.g. theme editor section reload) are GC-able.
  const cardState = new WeakMap();

  // Single source of truth for "should shimmer be active right now?". Used
  // both for the gsap.matchMedia gate and for section:load re-binding (which
  // fires outside the matchMedia callback). Width gate matches the rest of
  // Phase 0 — desktop animations live at ≥990px.
  const ACTIVATION_QUERY =
    '(min-width: 990px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)';

  function shouldActivate() {
    if (typeof gsap === 'undefined') return false;
    if (!window.matchMedia) return false;
    return window.matchMedia(ACTIVATION_QUERY).matches;
  }

  function initCard(card) {
    // Idempotent — second call on same card (e.g. shopify:section:load
    // firing for a section that wasn't actually unloaded) is a no-op.
    if (cardState.has(card)) return;

    const shimmer = card.querySelector('.ero-it__shimmer');
    if (!shimmer) return;

    // Prime --tilt-x/y on the card so the CSS transform that consumes
    // them (rotateX(var(--tilt-x)) on .ero-it__slide.active .ero-it__card)
    // resolves to a valid value from the moment this script attaches.
    // Without priming, the var would be undefined on the card itself
    // (CSS only declares --tilt-x on .ero-it__shimmer, which is a child)
    // and the rotateX() call would receive an invalid value.
    gsap.set(card, { '--tilt-x': '0deg', '--tilt-y': '0deg' });

    const tiltX = gsap.quickTo(card, '--tilt-x', { duration: 0.4, ease: 'power2.out' });
    const tiltY = gsap.quickTo(card, '--tilt-y', { duration: 0.4, ease: 'power2.out' });
    const shimmerX = gsap.quickTo(shimmer, '--shimmer-x', { duration: 0.2, ease: 'power2.out' });
    const shimmerY = gsap.quickTo(shimmer, '--shimmer-y', { duration: 0.2, ease: 'power2.out' });

    // Cached on pointerenter — getBoundingClientRect on every pointermove
    // would force a layout flush ~60+ times per second.
    let rect = null;

    function onEnter() {
      rect = card.getBoundingClientRect();
    }

    function onMove(e) {
      // Safety net: if for some reason move fires without a prior enter
      // (browser edge case, programmatic dispatch), recompute the rect.
      if (!rect) rect = card.getBoundingClientRect();

      const px = (e.clientX - rect.left) / rect.width;   // 0 to 1, left→right
      const py = (e.clientY - rect.top) / rect.height;   // 0 to 1, top→bottom

      // Tilt: standard direction, ±6° max.
      // Vertical pointer position drives X-axis rotation (rotateX).
      // Horizontal pointer position drives Y-axis rotation (rotateY).
      // tiltX is negated so pointer-toward-top tilts the card's top
      // edge AWAY from the viewer (standard "tilt toward cursor" feel).
      const tx = -((py - 0.5) * 2) * 8;
      const ty = ((px - 0.5) * 2) * 8;
      tiltX(tx);
      tiltY(ty);

      // Shimmer: opposite-direction parallax (Pokemon foil convention).
      // As pointer moves right (px → 1), shimmer-x goes more negative,
      // sliding the oversized conic background to the left — exposes a
      // different part of the gradient on screen, simulating tilt-and-
      // reflection physics. Range -100% to 0% keeps the conic rotation
      // center off-card at all positions (default static state is -50%).
      const sx = -(px * 250);
      const sy = -(py * 250);
      shimmerX(sx);
      shimmerY(sy);
    }

    function onLeave() {
      // Drift back to neutral via the same quickTos.
      tiltX(0);
      tiltY(0);
      shimmerX(-50);
      shimmerY(-50);
    }

    card.addEventListener('pointerenter', onEnter);
    card.addEventListener('pointermove', onMove);
    card.addEventListener('pointerleave', onLeave);

    cardState.set(card, {
      tiltX: tiltX,
      tiltY: tiltY,
      shimmerX: shimmerX,
      shimmerY: shimmerY,
      onEnter: onEnter,
      onMove: onMove,
      onLeave: onLeave
    });
  }

  function destroyCard(card) {
    const state = cardState.get(card);
    if (!state) return;
    card.removeEventListener('pointerenter', state.onEnter);
    card.removeEventListener('pointermove', state.onMove);
    card.removeEventListener('pointerleave', state.onLeave);
    cardState.delete(card);
  }

  function initAllCards(root) {
    const scope = root || document;
    const cards = scope.querySelectorAll('.ero-it__card');
    cards.forEach(initCard);
  }

  function destroyAllCards(root) {
    const scope = root || document;
    const cards = scope.querySelectorAll('.ero-it__card');
    cards.forEach(destroyCard);
  }

  function setup() {
    if (typeof gsap === 'undefined') return;

    const mm = gsap.matchMedia();
    mm.add(ACTIVATION_QUERY, function () {
      initAllCards();
      // matchMedia cleanup fires when the viewport crosses below 990px
      // (or the user enables reduced-motion, switches to a touch-only
      // device, etc.). GSAP auto-reverts quickTo'd CSS vars; we also
      // strip pointer listeners and forget cached rects so cold mobile
      // pointer events can't accidentally fire stale callbacks.
      return function () { destroyAllCards(); };
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  // Theme editor support — rebind on section reload, clean up on unload.
  // matchMedia handles viewport-cross cleanup; these handlers cover the
  // case where a single section's DOM re-renders without a viewport change.
  document.addEventListener('shopify:section:load', function (e) {
    if (!shouldActivate()) return;
    if (e.target) initAllCards(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!e.target) return;
    destroyAllCards(e.target);
  });
})();
